import { useTranslation } from 'react-i18next';

import { CountrySelectionList } from '../../../../components/CountrySelectionList';
import { RadioButton } from '../../../../components/RadioButton';
import { Skeleton } from '../../../../components/Skeleton';
import useVisibilityTransition from '../../../../hooks/useVisibilityTransition';
import { PreferencesInnerModal } from '../PreferencesInnerModal';

import { StyledCountriesList, StyledCountriesLoader, StyledCountriesRadiosWrapper } from './styles';
import { useCountriesPreferences } from './useCountriesPreferences';

type CountryPreferencesProps = {
  isOpen: boolean;
  onClose: () => void;
  onCloseInnerModal: () => void;
};

export const CountryPreferences = ({ isOpen, onClose, onCloseInnerModal }: CountryPreferencesProps) => {
  const { t } = useTranslation();

  const {
    isWorldwideAvailable,
    isWorldwideSelected,
    handleClickOnWorldWide,
    handleClickOnSpecificCountries,
    handleClickOnCountry,
    initiallyNotSelectedCountries,
    initiallySelectedCountries,
    selectedCountryIdsSet,
    handleSaveCountries,
    isSaveDisabled,
    isNoChangeInValues,
    isMutationLoading,
    isLoading,
  } = useCountriesPreferences(onClose);

  const isCountryListOpen = !isWorldwideAvailable || !isWorldwideSelected;

  const isTransitioning = useVisibilityTransition(isCountryListOpen);

  return (
    <PreferencesInnerModal
      isOpen={isOpen}
      onClose={onClose}
      onReturn={onCloseInnerModal}
      onSave={handleSaveCountries}
      title={t('insights.preferences.countries.title')}
      subtitle={t('insights.preferences.countries.subtitle')}
      tooltipContent={
        isNoChangeInValues
          ? t('insights.preferences.countries.no_change.tooltip')
          : t('insights.preferences.countries.disabled.tooltip')
      }
      isSaveDisabled={isSaveDisabled}
      isLoading={isMutationLoading}
    >
      {isLoading ? (
        <StyledCountriesLoader>
          <Skeleton width="95%" height="12px" />
          <Skeleton width="85%" height="12px" />
          <Skeleton width="55%" height="12px" />
        </StyledCountriesLoader>
      ) : (
        <>
          {isWorldwideAvailable ? (
            <StyledCountriesRadiosWrapper>
              <RadioButton
                label={t('insights.preferences.countries.worldwide')}
                selected={isWorldwideSelected}
                onClick={handleClickOnWorldWide}
              />
              <RadioButton
                label={t('insights.preferences.countries.specific')}
                selected={!isWorldwideSelected}
                onClick={handleClickOnSpecificCountries}
              />
            </StyledCountriesRadiosWrapper>
          ) : null}
          <StyledCountriesList isOpen={isCountryListOpen} isTransitioning={isTransitioning}>
            <CountrySelectionList
              initiallySelectedCountries={initiallySelectedCountries}
              initiallyNotSelectedCountries={initiallyNotSelectedCountries}
              selectedCountryIdsSet={selectedCountryIdsSet}
              handleClickOnCountry={handleClickOnCountry}
            />
          </StyledCountriesList>
        </>
      )}
    </PreferencesInnerModal>
  );
};
